/**
 * A edição do contrato, sem tela: o que a pessoa digitou vira o pedido ao
 * servidor, ou o motivo de não virar.
 *
 * A conferência daqui é a de formato. Baixar os usuários contratados abaixo do
 * que está em uso é aceito (decisão de 27/10/2026): ninguém perde o acesso, e o
 * limite só segura convite novo.
 *
 * Vai ao servidor só o campo que a pessoa mudou, comparado com o que a tela
 * mostrava quando ela abriu a edição, e não com o contrato de agora: se outra
 * pessoa salvou no meio, salvar aqui não desfaz a mudança dela num campo que
 * esta pessoa nem tocou.
 */

import type { AlterarContratoDaOrganizacao } from '@cliqcrm/contracts';
import { emMicros } from '@/shared/lib/formato';
import type { Dinheiro, MudancaDoContrato } from '../model/organizacao-detalhada';

/** O que os dois campos dizem, já conferido. */
export type ValoresDaEdicao = {
  readonly usuariosContratados: number;
  /** Nulo quando o campo ficou vazio, no contrato que ainda não tem esse preço. */
  readonly usuarioAdicional: Dinheiro | null;
};

export type LeituraDaEdicao =
  | { readonly ok: true; readonly valores: ValoresDaEdicao }
  | {
      readonly ok: false;
      readonly erros: { readonly usuarios?: string; readonly adicional?: string };
    };

const NUMERO_INVALIDO = 'Escreva um número inteiro, a partir de 1.';

/** "7" vira 7; o que não é inteiro a partir de 1 vira nulo. */
function inteiroDe(texto: string): number | null {
  const limpo = texto.trim();
  const numero = /^\d+$/.test(limpo) ? Number(limpo) : Number.NaN;
  return Number.isInteger(numero) && numero >= 1 ? numero : null;
}

/**
 * O campo de dinheiro: o valor, nulo quando vazio, ou o motivo. Com o valor de
 * hoje (`atual`), apagar é recusado: o servidor não deixa um valor voltar a
 * vazio, e salvar sem ele pareceria que o valor saiu.
 */
function dinheiroDe(
  texto: string,
  moeda: string,
  atual: Dinheiro | null,
  exemplo: string,
): { readonly valor: Dinheiro | null } | { readonly erro: string } {
  if (texto.trim() === '') {
    return atual
      ? { erro: 'O valor não pode ficar vazio. Escreva o novo valor.' }
      : { valor: null };
  }
  const micros = emMicros(texto);
  return micros === null
    ? { erro: `Escreva o valor em reais, como ${exemplo}.` }
    : { valor: { quantia_micros: micros, moeda } };
}

/**
 * "7" e "54,90" viram o pedido; "0" ou "abc" viram o motivo, campo a campo.
 *
 * Com o valor de hoje (`atual`), apagar o adicional é recusado: o servidor não
 * deixa um valor voltar a vazio, e salvar sem ele pareceria que o valor saiu.
 */
export function lerEdicao(
  usuarios: string,
  adicional: string,
  moeda: string,
  atual: Dinheiro | null = null,
): LeituraDaEdicao {
  const contratados = inteiroDe(usuarios);
  const valor = dinheiroDe(adicional, moeda, atual, '54,90');
  if (contratados === null || 'erro' in valor) {
    return {
      ok: false,
      erros: {
        ...(contratados === null ? { usuarios: NUMERO_INVALIDO } : {}),
        ...('erro' in valor ? { adicional: valor.erro } : {}),
      },
    };
  }
  return { ok: true, valores: { usuariosContratados: contratados, usuarioAdicional: valor.valor } };
}

/** O que os dois campos do armazenamento dizem, já conferido (#718). */
export type ValoresDoArmazenamento = {
  readonly armazenamentoContratadoGb: number;
  /** Nulo quando o campo ficou vazio, no contrato que ainda não tem esse preço. */
  readonly gbAdicional: Dinheiro | null;
};

export type LeituraDoArmazenamento =
  | { readonly ok: true; readonly valores: ValoresDoArmazenamento }
  | {
      readonly ok: false;
      readonly erros: { readonly armazenamento?: string; readonly gbAdicional?: string };
    };

/**
 * O armazenamento contratado e o valor do GB adicional (#718), com as regras
 * dos usuários: o GB inteiro a partir de 1, e o valor de hoje não fica vazio.
 * Baixar abaixo do que está em uso é aceito, como nos usuários: a cota passa a
 * segurar o envio de arquivos.
 */
export function lerEdicaoDoArmazenamento(
  armazenamento: string,
  gbAdicional: string,
  moeda: string,
  atual: Dinheiro | null = null,
): LeituraDoArmazenamento {
  const gb = inteiroDe(armazenamento);
  const valor = dinheiroDe(gbAdicional, moeda, atual, '2,99');
  if (gb === null || 'erro' in valor) {
    return {
      ok: false,
      erros: {
        ...(gb === null ? { armazenamento: NUMERO_INVALIDO } : {}),
        ...('erro' in valor ? { gbAdicional: valor.erro } : {}),
      },
    };
  }
  return { ok: true, valores: { armazenamentoContratadoGb: gb, gbAdicional: valor.valor } };
}

/** O valor deste cliente é outro que o do plano? */
export function ehPersonalizado(doCliente: Dinheiro | null, doPlano: Dinheiro | null): boolean {
  if (!doCliente || !doPlano) return false;
  return doCliente.quantia_micros !== doPlano.quantia_micros || doCliente.moeda !== doPlano.moeda;
}

/** O valor em reais, do jeito que a pessoa digita: "59,90". */
export function emReaisParaDigitar(valor: Dinheiro | null): string {
  if (!valor) return '';
  return (valor.quantia_micros / 1_000_000).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Só o que a pessoa mudou, comparado com o que estava nos campos quando a
 * edição abriu (`preenchido`). O valor com fração de centavo aparece
 * arredondado no campo: sem a pessoa mexer nele, ele não vai, e o preço não
 * muda sozinho.
 */
export function oQueMudou(
  valores: ValoresDaEdicao,
  preenchido: { readonly usuarios: string; readonly adicional: string },
): MudancaDoContrato {
  const mudanca: { usuariosContratados?: number; usuarioAdicional?: Dinheiro } = {};
  if (valores.usuariosContratados !== Number(preenchido.usuarios.trim())) {
    mudanca.usuariosContratados = valores.usuariosContratados;
  }
  const adicional = valores.usuarioAdicional;
  if (adicional && adicional.quantia_micros !== emMicros(preenchido.adicional)) {
    mudanca.usuarioAdicional = adicional;
  }
  return mudanca;
}

/** O mesmo de `oQueMudou`, para os campos do armazenamento. */
export function oQueMudouNoArmazenamento(
  valores: ValoresDoArmazenamento,
  preenchido: { readonly armazenamento: string; readonly gbAdicional: string },
): MudancaDoContrato {
  const mudanca: { armazenamentoContratadoGb?: number; gbAdicional?: Dinheiro } = {};
  if (valores.armazenamentoContratadoGb !== Number(preenchido.armazenamento.trim())) {
    mudanca.armazenamentoContratadoGb = valores.armazenamentoContratadoGb;
  }
  const gb = valores.gbAdicional;
  if (gb && gb.quantia_micros !== emMicros(preenchido.gbAdicional)) {
    mudanca.gbAdicional = gb;
  }
  return mudanca;
}

/** A mudança no formato do contrato da API. */
export function corpoDoContrato(mudanca: MudancaDoContrato): AlterarContratoDaOrganizacao {
  return {
    ...(mudanca.usuariosContratados === undefined
      ? {}
      : { usuarios_contratados: mudanca.usuariosContratados }),
    ...(mudanca.usuarioAdicional === undefined
      ? {}
      : {
          usuario_adicional: {
            quantia_micros: mudanca.usuarioAdicional.quantia_micros,
            moeda: 'BRL' as const,
          },
        }),
    ...(mudanca.armazenamentoContratadoGb === undefined
      ? {}
      : { armazenamento_gb: mudanca.armazenamentoContratadoGb }),
    ...(mudanca.gbAdicional === undefined
      ? {}
      : {
          gb_adicional: {
            quantia_micros: mudanca.gbAdicional.quantia_micros,
            moeda: 'BRL' as const,
          },
        }),
  };
}
